import type { ConfigState, ModeSelectMember } from '@renderer/types/configState'

/**
 * Determines the next page after Select page based on configuration.
 * Checks Select.enabled first, then Edit.enabled, otherwise returns Result.
 *
 * @param globalConfig - The global configuration state
 * @returns The next page to navigate to: 'select', 'edit', or 'result'
 */
export function getNextPageAfterSelect(
  globalConfig: ConfigState | null
): 'select' | 'edit' | 'result' {
  const selectEnabled = globalConfig?.pages.select.settings.enabled ?? false
  const editEnabled = globalConfig?.pages.edit.settings.enabled ?? false

  if (selectEnabled) return 'select'
  if (editEnabled) return 'edit'
  return 'result'
}

/**
 * Next page after Welcome, matching default /view flow.
 * Honors startWithPayment + applyAfterCapture (+ frame enabled).
 */
export function getNextPageAfterWelcome(
  globalConfig: ConfigState | null,
  startWithPayment = false
): string {
  if (startWithPayment) return 'checkout'

  const applyAfterCapture = globalConfig?.pages.filter?.settings?.applyAfterCapture || false
  if (applyAfterCapture) {
    const frameEnabled = globalConfig?.pages.frame?.settings?.enabled || false
    return frameEnabled ? 'frame' : 'capture'
  }

  return 'filter'
}

/**
 * Next page after Filter Continue.
 * applyAfterCapture → select (post-capture filter); otherwise → frame.
 */
export function getNextPageAfterFilter(globalConfig: ConfigState | null): string {
  const applyAfterCapture = globalConfig?.pages.filter?.settings?.applyAfterCapture || false
  return applyAfterCapture ? 'select' : 'frame'
}

/**
 * Next page after Frame Continue.
 * startWithPayment means checkout already happened → capture; else → checkout.
 */
export function getNextPageAfterFrame(startWithPayment = false): string {
  return startWithPayment ? 'capture' : 'checkout'
}

/**
 * Next page when leaving Checkout/Payment without needing the payment step
 * (cash settlement, zero total, or payment page disabled).
 * When startWithPayment, resume pre-capture flow via applyAfterCapture.
 */
export function getNextPageAfterPaymentFlow(
  globalConfig: ConfigState | null,
  options: {
    startWithPayment?: boolean
    mode?: 'capture' | 'record' | string | null
  } = {}
): string {
  const { startWithPayment = false, mode = 'capture' } = options
  if (startWithPayment) {
    const applyAfterCapture = globalConfig?.pages.filter?.settings?.applyAfterCapture || false
    return applyAfterCapture ? 'frame' : 'filter'
  }
  return mode === 'capture' ? 'capture' : 'record'
}

/**
 * Next page after Capture finishes (retake exhausted or disabled).
 */
export function getNextPageAfterCapture(
  globalConfig: ConfigState | null,
  canRetake: boolean
): string {
  const retakeEnabled = globalConfig?.pages.retake?.settings?.enabled ?? true
  if (canRetake && retakeEnabled) return 'retake'

  const applyAfterCapture = globalConfig?.pages.filter?.settings?.applyAfterCapture || false
  const filterEnabled = globalConfig?.pages.filter?.settings?.enabled
  if (applyAfterCapture && filterEnabled) return 'filter'

  return getNextPageAfterSelect(globalConfig)
}

/**
 * Whether Capture should bake the selected filter into the saved photo.
 *
 * With `applyAfterCapture` the guest picks a filter AFTER shooting, so capture must save
 * clean originals: baking in a not-yet-chosen filter (historically a silent fallback to
 * `filters[0]`) is irreversible, and it makes the later Filter selection a no-op because
 * the pixels are already burned.
 */
export function shouldBakeFilterAtCapture(globalConfig: ConfigState | null): boolean {
  return !(globalConfig?.pages.filter?.settings?.applyAfterCapture || false)
}

/**
 * Next page after Retake Continue / skip.
 */
export function getNextPageAfterRetake(globalConfig: ConfigState | null): string {
  const applyAfterCapture = globalConfig?.pages.filter?.settings?.applyAfterCapture || false
  const filterEnabled = globalConfig?.pages.filter?.settings?.enabled
  if (applyAfterCapture && filterEnabled) return 'filter'
  return getNextPageAfterSelect(globalConfig)
}

// ─── Multi-project hub ───────────────────────────────────────────────────────

/** The subset of hub context `getHomePath` needs (see stores/hubStore.ts). */
export interface HubContextLike {
  hubId?: string
  memberProjectId?: string
  /** 'mode' sends a finished session back to the picker instead of the Welcome. */
  returnTo?: 'welcome' | 'mode'
}

/**
 * The URL every in-booth navigation is built from: `/hub/{hubId}` while the
 * booth is on a hub route, `/{projectId}` otherwise.
 *
 * A hub route has no `:projectId` param, so the literal `/${projectId}` this
 * replaces produced `/undefined/mode-select` — the picker was unreachable from
 * the hub Welcome the moment hubs stopped being Projects.
 */
export function getRouteBasePath(params: { hubId?: string; projectId?: string }): string {
  return params.hubId ? `/hub/${params.hubId}` : `/${params.projectId ?? ''}`
}

/**
 * Active hub members in display order. Inactive members are hidden from the
 * picker; a member Studio dropped (deleted/deactivated project) never reaches
 * the booth because GET /api/projects omits it from `members`.
 */
export function getActiveHubMembers(globalConfig: ConfigState | null): ModeSelectMember[] {
  const members = globalConfig?.pages.modeSelect?.settings?.members
  if (!Array.isArray(members)) return []
  return members
    .filter((m) => m && typeof m.projectId === 'string' && m.projectId && m.isActive !== false)
    .map((m, index) => ({ m, index }))
    .sort((a, b) => {
      const ao = typeof a.m.order === 'number' ? a.m.order : a.index
      const bo = typeof b.m.order === 'number' ? b.m.order : b.index
      return ao - bo || a.index - b.index
    })
    .map(({ m }) => m)
}

/**
 * Where a Welcome "Start" goes. A hub with the picker enabled (and the
 * `multi_project_hub` flag on) sends the guest to `/mode-select` BEFORE any
 * session exists; everything else starts a session exactly as today. With no
 * active members the hub degrades to a normal Welcome rather than a blank
 * picker.
 */
export function resolveStartDestination(
  globalConfig: ConfigState | null,
  hubFeatureEnabled: boolean
): 'mode-select' | 'session' {
  if (!hubFeatureEnabled) return 'session'
  if (!globalConfig?.pages.modeSelect?.settings?.enabled) return 'session'
  return getActiveHubMembers(globalConfig).length > 0 ? 'mode-select' : 'session'
}

/**
 * The "home" route for a project: `/hub/{hubId}` while `projectId` is the
 * member the booth entered from that hub, else `/{projectId}`. Every session
 * exit (finish, timeout, idle, operator exit, no-session redirect) goes through
 * this so the NEXT guest lands on the hub Welcome, never on the member's.
 *
 * `projectId` is the raw route param on purpose — in digital mode it is a slug
 * and must stay in the address bar (see useSessionReset).
 */
export function getHomePath(
  projectId: string | undefined,
  hub: HubContextLike | null | undefined
): string {
  if (projectId && hub?.hubId && hub.memberProjectId && hub.memberProjectId === projectId) {
    // 'mode' skips the hub Welcome so the next guest lands straight on the
    // picker. The picker's own timeout still falls back to the Welcome, so a
    // booth left alone does not sit on a half-way screen forever.
    return hub.returnTo === 'mode' ? `/hub/${hub.hubId}/mode-select` : `/hub/${hub.hubId}`
  }
  return `/${projectId ?? ''}`
}
